import { useState, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import { clickSendCollectionReminderService, SendCollectionReminderWARequest } from '../services/clickSendCollectionReminderService';
import { authFetch } from '../utils/authFetch';
import { getCustomerPhoneData, CustomerPhoneData } from '../utils/customerPhoneUtils';

export interface UseWhatsAppSendReturn {
  sendCollectionReminderWA: (
    conversationId: string,
    templateName: string,
    templateVariables: Record<string, string>
  ) => Promise<{ success: boolean; message?: string; error?: string }>;
  createConversation: (
    customerId: string,
    phoneNumber: string,
    customerName: string
  ) => Promise<{ success: boolean; conversationId?: string; error?: string }>;
  getCustomerPhoneData: (customer: any) => CustomerPhoneData | null;
  checkExistingConversation: (customerId: string) => Promise<any>;
  loading: boolean;
  error: string | null;
}

export const useWhatsAppSend = (organizationId: string): UseWhatsAppSendReturn => {
  const { accessToken, setUserAndToken } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCollectionReminderWA = useCallback(async (
    conversationId: string,
    templateName: string,
    templateVariables: Record<string, string>
  ) => {
    try {
      setLoading(true);
      setError(null);

      if (!accessToken) {
        throw new Error('Authentication required');
      }

      const request: SendCollectionReminderWARequest = {
        conversationId,
        templateName,
        templateVariables
      };

      const result = await clickSendCollectionReminderService.sendCollectionReminderWA(
        request,
        accessToken,
        setUserAndToken
      );

      return result;
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send collection reminder';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, setUserAndToken]);

  const createConversation = useCallback(async (
    customerId: string,
    phoneNumber: string,
    customerName: string
  ) => {
    try {
      setLoading(true);
      setError(null);

      if (!accessToken) {
        throw new Error('Authentication required');
      }

      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: organizationId,
            pic_contact: { whatsapp: phoneNumber }, // Use dict format to match backend expectations
            pic_name: customerName,
            channel: 'whatsapp',
            xero_direct_contact_id: customerId,
            force_new: true  // Always create new conversation to avoid updating existing ones
          })
        },
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const conversation = await response.json();
        return {
          success: true,
          conversationId: conversation.id
        };
      } else {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          error: errorData.detail || 'Failed to create conversation'
        };
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create conversation';
      setError(errorMessage);
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [organizationId, accessToken, setUserAndToken]);


  const checkExistingConversation = useCallback(async (customerId: string) => {
    try {
      // Use the new endpoint that looks up by xero_direct_contact_id
      // This now returns a list of all conversations for this customer
      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000'}/api/v1/admin/conversations/xero_direct_contact_id/${customerId}`,
        {},
        accessToken,
        setUserAndToken
      );

      if (response.ok) {
        const conversations = await response.json();
        // Return the list of conversations (can be empty array)
        return Array.isArray(conversations) ? conversations : [];
      }
      // 404 or other error means no conversations found for this xero_direct_contact_id
      return [];
    } catch (err) {
      console.error('Error checking existing conversation:', err);
      return [];
    }
  }, [accessToken, setUserAndToken]);

  return {
    sendCollectionReminderWA,
    createConversation,
    getCustomerPhoneData,
    checkExistingConversation,
    loading,
    error
  };
};
